import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../css/styles.css";
import { FaUserCircle, FaQuoteLeft, FaStar, FaCalendarAlt, FaArrowRight, FaChevronLeft, FaChevronRight, FaGlobeAfrica, FaUsers, FaPaperPlane } from "react-icons/fa";
import { API_BASE, parseJsonResponse } from "../api";

const HERO_VIDEO = "https://res.cloudinary.com/dn39bkkiq/video/upload/q_auto/v1776348496/Homepagevid_xuvuof.mp4";

const cloudImg = (path) =>
  `https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_800/${path}`;

const featuredSafaris = [
  {
    img: cloudImg("v1776348869/3_Days_Masai_mara_Express_Safari_2_gjcw8a.jpg"),
    title: "3 Days Masai Mara Express Safari",
    duration: "3 Days, 2 Nights",
    route: "/wildebeest-migration",
  },
  {
    img: cloudImg("v1776348587/6_Day_Kilimanjaro_Tsavo_Safari_kwzzin.jpg"),
    title: "Kilimanjaro Classic Safari + Diani Beach Extension",
    duration: "6 Days, 5 Nights",
    route: "/kilimanjaro-tsavo",
  },
  {
    img: cloudImg("v1776348685/8_Day_Best_of_Kenya_Luxury_Flying_Safari_Diani_Beach_2_g1mr45.jpg"),
    title: "8 Day Best of Kenya Luxury Flying Safari + Diani Beach",
    duration: "8 Days, 7 Nights",
    route: "/luxury-flying-safari",
  },
  {
    img: cloudImg("v1776348741/8_Days_Wild_Heart_Kenya_Safari_u0ichl.jpg"),
    title: "8 Day Kenya Wild Heart Safari",
    duration: "8 Days, 7 Nights",
    route: "/wild-heart-kenya",
  },
  {
    img: cloudImg("v1776348883/10_Day_Kenya_Tanzania_Highlights_osh7bf.jpg"),
    title: "10 Day Kenya & Tanzania Highlights",
    duration: "10 Days, 9 Nights",
    route: "/kenya-tanzania-highlights",
  },
  {
    img: cloudImg("v1776348825/7_Day_Northern_Tanzania_Safari_mvpqrt.jpg"),
    title: "7 Day Northern Tanzania Safari",
    duration: "7 Days, 6 Nights",
    route: "/northern-tanzania",
  },
];

const testimonials = [
  {
    name: "Sammy Smith",
    location: "United States",
    text: "Nyatwende Travel and Tours provided an amazing safari experience! The team was professional and made sure everything was seamless.",
    rating: 5,
  },
  {
    name: "Sarah Wanjiru",
    location: "Kenya",
    text: "Our family vacation was perfectly planned, and we had a stress-free experience thanks to Nyatwende Travel.",
    rating: 5,
  },
  {
    name: "Donald Okafor",
    location: "Nigeria",
    text: "I highly recommend Nyatwende Travel for international trips. They made visa processing and bookings so easy!",
    rating: 5,
  },
];

const Home = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const videoRef = useRef(null);
  const [ctaForm, setCtaForm] = useState({
    name: "",
    email: "",
    destination: "",
    travelers: "",
    travelDate: "",
    message: "",
  });
  const [ctaSubmitted, setCtaSubmitted] = useState(false);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [ctaError, setCtaError] = useState("");

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleCtaChange = (e) => {
    setCtaForm({ ...ctaForm, [e.target.name]: e.target.value });
  };

  const handleCtaSubmit = async (e) => {
    e.preventDefault();
    setCtaLoading(true);
    setCtaError("");
    try {
      const res = await fetch(`${API_BASE}/api/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: ctaForm.name,
          email: ctaForm.email,
          destination: ctaForm.destination,
          travelers: ctaForm.travelers,
          date: ctaForm.travelDate,
          message: ctaForm.message,
        }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        throw new Error(data.error || data.message || `Request failed ${res.status}`);
      }
      setCtaSubmitted(true);
      setCtaForm({ name: "", email: "", destination: "", travelers: "", travelDate: "", message: "" });
      setTimeout(() => setCtaSubmitted(false), 5000);
    } catch (err) {
      setCtaError(err.message);
    } finally {
      setCtaLoading(false);
    }
  };

  return (
    <div className="home-container">
      {/* ===== SECTION 1: HERO (VIDEO) ===== */}
      <section className="hero-section">
        <div className="hero-video-wrap">
          <video
            ref={videoRef}
            className="hero-video"
            src={HERO_VIDEO}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        </div>
        <div className="hero-overlay">
          <div className="hero-content">
            <h1 className="hero-headline">
              Tailor Made African Safaris<br />Designed by Experts
            </h1>
            <p className="hero-subtitle">
              Tailor-made safaris across Kenya, Tanzania and Zanzibar with carefully
              selected lodges and unforgettable wildlife experiences.
            </p>
            <blockquote className="hero-quote">
              <em>
                "East Africa is where the earth meets the sky, and every moment feels alive."
              </em>
              <br />
              <em>
                "Golden savannahs stretch as far as the eye can see, where lions lounge in
                the sun and elephants wander past acacia trees. From misty mountains to
                sparkling turquoise beaches, East Africa invites you to explore, to wander,
                and to lose yourself in its beauty."
              </em>
            </blockquote>
            <div className="hero-buttons">
              <Link to="/tours" className="hero-cta primary">
                Explore Our Safari Packages
              </Link>
              <button
                onClick={() => document.getElementById("plan-safari")?.scrollIntoView({ behavior: "smooth" })}
                className="hero-cta secondary"
              >
                Plan My Safari
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 2: FEATURED SAFARIS ===== */}
      <section className="featured-safaris-section">
        <span className="section-tag centered">Our Top Picks</span>
        <h2 className="section-heading">Featured Safaris</h2>
        <p className="section-subheading">Handcrafted itineraries across East Africa's most iconic destinations</p>
        <div className="safaris-grid">
          {featuredSafaris.map((safari, index) => (
            <Link to={safari.route} key={index} className="safari-card">
              <img src={safari.img} alt={safari.title} className="safari-card-bg" />
              <div className="safari-card-overlay" />
              <div className="safari-card-content">
                <span className="safari-card-badge">
                  <FaCalendarAlt /> {safari.duration}
                </span>
                <h3 className="safari-card-title">{safari.title}</h3>
                <span className="safari-card-cta">
                  View Itinerary <FaArrowRight />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="safaris-view-all">
          <Link to="/tours" className="hero-cta primary">
            View All Safaris <FaArrowRight className="btn-icon" />
          </Link>
        </div>
      </section>

      {/* ===== SECTION 3: TESTIMONIALS ===== */}
      <section className="testimonials-section">
        <div className="testimonials-inner">
          <div className="testimonials-header">
            <span className="section-tag light">Reviews</span>
            <h2>What Our Travelers Say</h2>
            <p className="testimonials-subtitle">
              Real stories from real adventurers who trusted us with their dream safari
            </p>
          </div>

          <div className="testimonial-spotlight">
            <button className="testimonial-nav prev" onClick={prevTestimonial} aria-label="Previous review">
              <FaChevronLeft />
            </button>

            <div className="testimonial-stage">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`testimonial-slide ${index === activeTestimonial ? "active" : ""}`}
                >
                  <div className="testimonial-quote-mark">
                    <FaQuoteLeft />
                  </div>
                  <blockquote className="testimonial-text">
                    {testimonial.text}
                  </blockquote>
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <FaStar key={i} className="star-icon" />
                    ))}
                  </div>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">
                      <FaUserCircle />
                    </div>
                    <div>
                      <h4 className="testimonial-name">{testimonial.name}</h4>
                      <span className="testimonial-location">{testimonial.location}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button className="testimonial-nav next" onClick={nextTestimonial} aria-label="Next review">
              <FaChevronRight />
            </button>
          </div>

          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`testimonial-dot ${index === activeTestimonial ? "active" : ""}`}
                onClick={() => setActiveTestimonial(index)}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: CTA FORM ===== */}
      <section className="cta-section cta-clean" id="plan-safari">
        <div className="cta-content">
          <span className="cta-eyebrow">Your Safari, Your Way</span>
          <h2>Not Sure Where to Start?</h2>
          <p>
            Tell us about your dream trip and we'll design the perfect safari —
            handpicked lodges, private game drives, and moments you'll never forget.
          </p>

          {ctaError && (
            <div className="modern-form-error">
              <p>{ctaError}</p>
            </div>
          )}

          {ctaSubmitted ? (
            <div className="modern-form-success">
              <div className="success-checkmark">&#10003;</div>
              <h3>Request Sent!</h3>
              <p>Our safari experts will reach out shortly.</p>
            </div>
          ) : (
            <form className="modern-form cta-inline-form" onSubmit={handleCtaSubmit}>
              <div className="modern-form-grid">
                <div className="modern-form-group">
                  <label htmlFor="cta-name">Full Name</label>
                  <input
                    id="cta-name"
                    type="text"
                    name="name"
                    placeholder="Your full name"
                    value={ctaForm.name}
                    onChange={handleCtaChange}
                    required
                  />
                </div>
                <div className="modern-form-group">
                  <label htmlFor="cta-email">Email Address</label>
                  <input
                    id="cta-email"
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={ctaForm.email}
                    onChange={handleCtaChange}
                    required
                  />
                </div>
                <div className="modern-form-group">
                  <label htmlFor="cta-destination">
                    <FaGlobeAfrica className="field-icon" /> Where To?
                  </label>
                  <select
                    id="cta-destination"
                    name="destination"
                    value={ctaForm.destination}
                    onChange={handleCtaChange}
                  >
                    <option value="">Pick a destination</option>
                    <option value="masai-mara">Masai Mara</option>
                    <option value="amboseli">Amboseli</option>
                    <option value="kilimanjaro">Kilimanjaro</option>
                    <option value="serengeti">Serengeti</option>
                    <option value="zanzibar">Zanzibar</option>
                    <option value="combo">Kenya & Tanzania</option>
                    <option value="other">Not Sure Yet</option>
                  </select>
                </div>
                <div className="modern-form-group">
                  <label htmlFor="cta-travelers">
                    <FaUsers className="field-icon" /> Travelers
                  </label>
                  <select
                    id="cta-travelers"
                    name="travelers"
                    value={ctaForm.travelers}
                    onChange={handleCtaChange}
                  >
                    <option value="">How many?</option>
                    <option value="1">Solo</option>
                    <option value="2">2 People</option>
                    <option value="3-5">3 - 5</option>
                    <option value="6-10">6 - 10</option>
                    <option value="10+">10+ Group</option>
                  </select>
                </div>
                <div className="modern-form-group">
                  <label htmlFor="cta-date">
                    <FaCalendarAlt className="field-icon" /> Travel Date
                  </label>
                  <input
                    id="cta-date"
                    type="date"
                    name="travelDate"
                    value={ctaForm.travelDate}
                    onChange={handleCtaChange}
                  />
                </div>
              </div>
              <div className="modern-form-group full-width">
                <label htmlFor="cta-message">Tell Us More</label>
                <textarea
                  id="cta-message"
                  name="message"
                  rows="4"
                  placeholder="Any special interests, budget, or questions?"
                  value={ctaForm.message}
                  onChange={handleCtaChange}
                />
              </div>
              <button type="submit" className="modern-form-submit cta-form-submit" disabled={ctaLoading}>
                {ctaLoading ? (
                  <span className="spinner-wrapper"><span className="spinner" /> Sending...</span>
                ) : (
                  <><FaPaperPlane /> Plan My Safari</>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;